import { icons } from "assets/icons";
import DefaultCell from "components/DataTable/cells/DefaultCell";
import { datatableStyle } from "components/DataTable/components/DataTableStyling.config";
import { StyledDataTableWrapper } from "components/DataTable/style";
import LoadingScreen from "components/Loading/LoadingScreen";
import { GenericModal } from "components/Modals/GenericModal";
import { Tooltip } from "components/Tooltip";
import { useUserRolePermissionStore, useWorkgroupsStore } from "contexts/RootStore.context";
import { Content, Horizontal, Vertical } from "gls/lib";
import { observer } from "mobx-react";
import { useCallback, useMemo } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import { colours } from "styling/colours";
import { style } from "typestyle";
import { useWorkgroups } from "../hooks/useWorkgroups";
import { WorkgroupDto } from "../WorkgroupDto";
import { EditWorkgroupNameModal } from "./EditWorkgroupNameModal";
import { RowActions } from "./RowActions";
import { getWorkgroupAssociatedUsers } from "api/config/workgroup";

const WorkgroupList = observer(() => {
  const { workgroups, workgroupsLoading } = useWorkgroupsStore();
  const { canManageGroupsAndRoles } = useUserRolePermissionStore();
  const {
    changeWorkgroupName,
    currentWorkgroup,
    deleteOpen,
    handleEditOnClose,
    handleOnEdit,
    isDeleting,
    setCurrentWorkgroup,
    setDeleteOpen,
    updateNameModalOpen,
    workgroupDelete,
    apiLoading,
    setAssociatedUsers,
    associatedUsers,
  } = useWorkgroups();

  const renderAdministrators = (row: WorkgroupDto) =>
    row.administrators?.length > 0
      ? row.administrators.join(", ")
      : "--";

  const handleDelete = useCallback((workgroup: WorkgroupDto) => {
    getWorkgroupAssociatedUsers(workgroup.id)
      .then((users) => {
        setAssociatedUsers(users.length)
        setCurrentWorkgroup(workgroup);
        setDeleteOpen(true);
      })
  }, [setAssociatedUsers, setCurrentWorkgroup, setDeleteOpen])

  const columns = useMemo(() => {
    return [
      {
        name: 'NAME',
        selector: (row: WorkgroupDto) => <DefaultCell value={row.name} />,
        minWidth: '240px',
        maxWidth: '410px',
      },
      {
        name: 'ADMINISTRATORS',
        selector: (row: WorkgroupDto) =>
          <Tooltip
            content={renderAdministrators(row)}
            contentClassName={ellipsis}
            place="left"
            key={`administrators-column-${row.id}`}
          >
            {row.administrators.length > 0
              ? row.administrators.map(admin => <div>{admin}</div>)
              : <div>-</div>
            }
          </Tooltip>,
        minWidth: '180px',
        overflow: 'true',
      },
      {
        right: true,
        sortable: false,
        cell: (row: WorkgroupDto) =>
          <RowActions
            key={`row-actions-${row.id}`}
            workgroup={row}
            handleToggleDelete={handleDelete}
            canManage={canManageGroupsAndRoles}
            onEdit={handleOnEdit}
          />,
        width: '200px',
      },
    ] as TableColumn<WorkgroupDto>[];
  }, [canManageGroupsAndRoles, handleDelete, handleOnEdit])

  if (workgroupsLoading)
    return <LoadingScreen />

  if (workgroups.length === 0)
    return (
      <Content horizontalAlign="center" padding={40}>
        No Workgroups
      </Content>
    )

  return (
    <>
      <StyledDataTableWrapper
        data-test-id="table"
        className={`table-container`}
      >
        <DataTable
          columns={columns}
          data={workgroups}
          defaultSortFieldId={4}
          defaultSortAsc={false}
          customStyles={datatableStyle}
        />
      </StyledDataTableWrapper>

      <GenericModal
        id='delete-modal'
        title="Delete"
        subtitle={
          <Vertical spacing={12}>
            <span className={modalTextStyle}>
              Are you sure you want to permanently delete the &nbsp;
              <strong>{currentWorkgroup?.name} workgroup</strong> and the
              roles associated with it?
            </span>
            <Horizontal verticalAlign="top" spacing={6}>
              <span>
                <icons.InfoIcon aria-label="note" fill={colours.secondaryText} />
              </span>
              <span className={modalTextStyle}>
                Please note there are <b>{associatedUsers}</b> users associated to this workgroup/role. The
                association will be removed if you delete the workgroup.
              </span>
            </Horizontal>
          </Vertical>
        }
        actionName="Delete"
        actionStyle="cancel"
        icon={<icons.OutlineDeleteIcon aria-label="Delete Workgroup" className={ModalIconStyle} height={40} width={40} />}
        roundIconStyle={false}
        isOpen={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onAction={workgroupDelete}
        loading={isDeleting}
      />
      <EditWorkgroupNameModal
        isOpen={updateNameModalOpen}
        onClose={handleEditOnClose}
        defaultValue={currentWorkgroup?.name || ""}
        onSubmit={(workgroupName: string) => {
          changeWorkgroupName(workgroupName);
        }}
        loading={apiLoading}
      />
    </>
  );
});

export default WorkgroupList;

const modalTextStyle = style({
  color: colours.primaryText,
});

const ModalIconStyle = style({
  border: "1px solid red",
  borderRadius: "50%",
  padding: "7px",
});

const ellipsis = style({
  display: "block",
  overflow: "hidden",
  textOverflow: "ellipsis",
  verticalAlign: "middle",
  whiteSpace: "nowrap",
});